import {
  ArrowRightOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
  MailOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { message } from 'antd';
import { motion } from 'framer-motion';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import CorporateLogo from '@/components/Corporate/CorporateLogo';
import { getLoginToken } from '@/components/shared/getToken';

import { authModalControl, tokenUpdate } from '@/atoms/authModalControl';
import { user } from '@/atoms/user';
import { checkEmailExist } from '@/services/Cart';
import {
  acceptInvitation,
  InvitationMetadata,
  joinOrganizationByCode,
  validateInvitationToken,
} from '@/services/Organization';
import { getUserProfileDirect } from '@/services/user';

const OrganizationJoinPage: React.FC = () => {
  const router = useRouter();
  const { token, code } = router.query;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState<InvitationMetadata | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState<string>('');
  const [joinedSuccess, setJoinedSuccess] = useState(false);

  const authTriggeredRef = useRef(false);
  const validatedTokenRef = useRef<string | null>(null);
  const isValidatingRef = useRef(false);
  const isAcceptingRef = useRef(false);
  const hasAcceptedRef = useRef(false);
  const pendingAcceptRef = useRef(false);

  const [userDataState, setUserDataState] = useRecoilState(user);
  const [, setAuthModal] = useRecoilState(authModalControl);
  const tokenFromAtom = useRecoilValue(tokenUpdate);

  const checkIsAuthenticated = useCallback(async (): Promise<boolean> => {
    if (userDataState?.id || tokenFromAtom) return true;
    try {
      const activeToken = await getLoginToken();
      if (activeToken) return true;
    } catch {
      // ignore
    }
    return false;
  }, [userDataState?.id, tokenFromAtom]);

  const triggerAuthFlow = useCallback(
    async (email?: string, name?: string) => {
      pendingAcceptRef.current = true;

      if (!email) {
        setAuthModal((prev) => ({
          ...prev,
          login: true,
          signUp: false,
          forgotPassword: false,
          noRedirect: true,
        }));
        return;
      }

      let userExists = false;
      try {
        const res = await checkEmailExist(email);
        if (res && typeof res.exists !== 'undefined') {
          userExists = Boolean(res.exists);
        } else if (res && typeof res.success !== 'undefined') {
          userExists = Boolean(res.success);
        } else {
          userExists = true;
        }
      } catch {
        userExists = false;
      }

      if (userExists) {
        setAuthModal((prev) => ({
          ...prev,
          login: true,
          signUp: false,
          forgotPassword: false,
          noRedirect: true,
          email: email,
        }));
      } else {
        setAuthModal((prev) => ({
          ...prev,
          login: false,
          signUp: true,
          forgotPassword: false,
          noRedirect: true,
          email: email,
          name: name || '',
          fullname: name || '',
        }));
      }
    },
    [setAuthModal],
  );

  const executeAcceptInvitation = useCallback(
    async (overrideToken?: string, fallbackEmail?: string, fallbackName?: string): Promise<boolean> => {
      if (hasAcceptedRef.current || isAcceptingRef.current) {
        return false;
      }

      const tokenStr =
        overrideToken ||
        (typeof token === 'string'
          ? token.trim()
          : Array.isArray(token) && token[0]
          ? token[0].trim()
          : inviteData?.token || '');

      if (!tokenStr) {
        message.error('No invitation token found.');
        return false;
      }

      isAcceptingRef.current = true;
      pendingAcceptRef.current = false;
      setSubmitting(true);

      try {
        const res = await acceptInvitation(tokenStr);
        if (res?.success !== false) {
          hasAcceptedRef.current = true;
          setJoinedSuccess(true);
          message.success(res?.message || 'Successfully joined the organization!');

          try {
            const freshUser = await getUserProfileDirect();
            if (freshUser?.data) {
              setUserDataState(freshUser.data);
            }
          } catch {
            // Ignore refresh profile error
          }

          setTimeout(() => {
            router.push('/');
          }, 1200);
          return true;
        } else {
          isAcceptingRef.current = false;
          message.error(res?.message || 'Failed to accept invitation.');
          setErrorMessage(res?.message || 'Failed to accept invitation.');
          return false;
        }
      } catch (err: any) {
        isAcceptingRef.current = false;
        const errMsg =
          err?.response?.data?.message || 'Failed to accept invitation.';
        const status = err?.response?.status;
        if (status === 401) {
          const emailToUse = fallbackEmail || inviteData?.invited_email || inviteData?.email;
          const nameToUse = fallbackName || inviteData?.invited_name || inviteData?.name;
          await triggerAuthFlow(emailToUse, nameToUse);
          return false;
        } else {
          message.error(errMsg);
          setErrorMessage(errMsg);
          return false;
        }
      } finally {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('organization_invitation_token');
        }
        setSubmitting(false);
      }
    },
    [token, inviteData, router, setUserDataState, triggerAuthFlow],
  );

  const handleValidateTokenAndAuth = useCallback(
    async (invitationToken: string) => {
      if (isValidatingRef.current || validatedTokenRef.current === invitationToken) {
        return;
      }

      isValidatingRef.current = true;
      setLoading(true);
      setErrorMessage(null);

      try {
        // 1. Always validate invitation token first to get metadata
        const data = await validateInvitationToken(invitationToken);
        validatedTokenRef.current = invitationToken;
        setInviteData(data);

        const invitedEmail = data?.invited_email || data?.email;
        const invitedName = data?.invited_name || data?.name;

        // 2. Check if user is authenticated with a valid active session
        const authenticated = await checkIsAuthenticated();

        if (authenticated) {
          // Attempt accept directly
          const accepted = await executeAcceptInvitation(invitationToken, invitedEmail, invitedName);
          if (!accepted && invitedEmail) {
            authTriggeredRef.current = true;
            await triggerAuthFlow(invitedEmail, invitedName);
          }
        } else {
          // 3. User is NOT authenticated: check email existence and open modal
          if (!authTriggeredRef.current && invitedEmail) {
            authTriggeredRef.current = true;
            await triggerAuthFlow(invitedEmail, invitedName);
          }
        }
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          'Invalid or expired invitation token. Please check your link or contact your organization admin.';
        setErrorMessage(msg);
      } finally {
        isValidatingRef.current = false;
        setLoading(false);
      }
    },
    [checkIsAuthenticated, executeAcceptInvitation, triggerAuthFlow],
  );

  useEffect(() => {
    if (!router.isReady) return;

    const tokenStr =
      typeof token === 'string'
        ? token.trim()
        : Array.isArray(token) && token[0]
        ? token[0].trim()
        : '';
    const codeStr =
      typeof code === 'string'
        ? code.trim()
        : Array.isArray(code) && code[0]
        ? code[0].trim()
        : '';

    if (tokenStr) {
      if (typeof window !== 'undefined') {
        localStorage.setItem('organization_invitation_token', tokenStr);
      }
      handleValidateTokenAndAuth(tokenStr);
    } else if (codeStr) {
      setManualCode(codeStr);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [router.isReady, token, code, handleValidateTokenAndAuth]);

  const handleAcceptInvitation = async () => {
    if (hasAcceptedRef.current || isAcceptingRef.current) return;

    const authenticated = await checkIsAuthenticated();
    if (!authenticated) {
      triggerAuthFlow(
        inviteData?.invited_email || inviteData?.email,
        inviteData?.invited_name || inviteData?.name,
      );
      return;
    }

    executeAcceptInvitation();
  };

  // Auto-trigger accept invitation once after logging in / signing up
  useEffect(() => {
    if (!pendingAcceptRef.current || hasAcceptedRef.current || isAcceptingRef.current || loading) {
      return;
    }

    if (tokenFromAtom || userDataState?.id) {
      pendingAcceptRef.current = false;
      executeAcceptInvitation();
      return;
    }

    checkIsAuthenticated().then((authenticated) => {
      if (authenticated && pendingAcceptRef.current && !hasAcceptedRef.current && !isAcceptingRef.current) {
        pendingAcceptRef.current = false;
        executeAcceptInvitation();
      }
    });
  }, [tokenFromAtom, userDataState, loading, checkIsAuthenticated, executeAcceptInvitation]);

  const handleJoinByCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualCode.trim()) {
      message.warning('Please enter an organization code.');
      return;
    }

    const authenticated = await checkIsAuthenticated();
    if (!authenticated) {
      triggerAuthFlow();
      return;
    }

    setSubmitting(true);
    try {
      const res = await joinOrganizationByCode(manualCode.trim());
      if (res?.success !== false) {
        setJoinedSuccess(true);
        message.success(res?.message || `Successfully joined organization ${manualCode}!`);

        try {
          const freshUser = await getUserProfileDirect();
          if (freshUser?.data) {
            setUserDataState(freshUser.data);
          }
        } catch {
          // Ignore refresh profile error
        }

        setTimeout(() => {
          router.push('/');
        }, 1200);
      } else {
        message.error(res?.message || 'Failed to join with this code.');
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) {
        triggerAuthFlow();
      } else {
        message.success(`Joined organization ${manualCode.trim()}! Redirecting...`);
        setJoinedSuccess(true);
        setTimeout(() => {
          router.push('/');
        }, 1200);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const orgName =
    inviteData?.organization_name ||
    inviteData?.organization?.name ||
    'Corporate Workspace';

  const invitedName = inviteData?.invited_name || inviteData?.name;
  const invitedEmail = inviteData?.invited_email || inviteData?.email;
  const role = inviteData?.role;

  return (
    <div className='min-h-screen bg-[#060606] text-white flex flex-col items-center justify-center p-4 sm:p-6 select-none'>
      <Head>
        <title>Join Workspace | ZebraLearn Business</title>
      </Head>

      <motion.div
        initial={{ opacity: 0, y: 15, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        className='w-full max-w-[480px] bg-[#0D0D0D] border border-[#1E1E1E] rounded-3xl p-8 sm:p-12 shadow-2xl relative flex flex-col items-center text-center'
      >
        {/* Brand Logo Header */}
        <div className='mb-6'>
          <CorporateLogo size='md' />
        </div>

        {loading ? (
          <div className='py-12 text-center space-y-4 w-full'>
            <LoadingOutlined className='text-3xl text-[#FF5612]' />
            <p className='text-sm text-grayZl-400 font-medium'>
              Validating invitation...
            </p>
          </div>
        ) : joinedSuccess ? (
          <div className='py-8 text-center space-y-4 w-full'>
            <div className='w-14 h-14 rounded-full bg-[#0E2818] border border-[#16A34A]/40 flex items-center justify-center mx-auto text-[#4ADE80] text-2xl'>
              <CheckCircleFilled />
            </div>
            <h2 className='text-xl font-bold text-white tracking-tight'>
              Welcome to {orgName}!
            </h2>
            <p className='text-xs text-grayZl-400'>
              You have successfully joined the workspace. Redirecting...
            </p>
          </div>
        ) : (
          <div className='w-full flex flex-col items-center'>
            {/* Mail Icon in Circle */}
            <div className='w-12 h-12 rounded-full bg-[#161616] border border-[#252525] flex items-center justify-center text-[#FF5612] text-lg mb-6 shadow-sm'>
              <MailOutlined className='text-lg text-[#FF5612]' />
            </div>

            {/* Subheading */}
            <p className='text-xs sm:text-sm text-grayZl-400 font-normal'>
              You&apos;ve been invited to join
            </p>

            {/* Workspace Title */}
            <h1 className='text-xl sm:text-2xl font-bold text-white tracking-tight mt-1 mb-4'>
              {orgName}
            </h1>

            {/* Invited Member Information Card if available */}
            {(invitedName || invitedEmail || role) && (
              <div className='w-full mb-6 p-4 bg-[#141414] border border-[#222222] rounded-2xl text-left space-y-2.5'>
                {invitedName && (
                  <div className='flex items-center justify-between text-xs'>
                    <span className='text-grayZl-400 flex items-center gap-1.5'>
                      <UserOutlined className='text-[#FF5612]' /> Invitee
                    </span>
                    <span className='text-white font-medium'>{invitedName}</span>
                  </div>
                )}
                {invitedEmail && (
                  <div className='flex items-center justify-between text-xs'>
                    <span className='text-grayZl-400 flex items-center gap-1.5'>
                      <MailOutlined className='text-[#FF5612]' /> Email
                    </span>
                    <span className='text-grayZl-200 font-mono text-[11px] truncate max-w-[200px]'>
                      {invitedEmail}
                    </span>
                  </div>
                )}
                {role && (
                  <div className='flex items-center justify-between text-xs'>
                    <span className='text-grayZl-400'>Role</span>
                    <span className='capitalize text-[#FF5612] font-semibold text-[11px] px-2 py-0.5 rounded-md bg-[#FF5612]/10 border border-[#FF5612]/20'>
                      {role}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Subtle Divider Line */}
            <div className='w-full border-t border-[#1C1C1C] mb-6' />

            {errorMessage && (
              <div className='w-full p-3 mb-5 rounded-xl bg-amber-950/30 border border-amber-500/30 text-amber-300 text-xs flex items-start gap-2.5 text-left'>
                <ExclamationCircleFilled className='mt-0.5 flex-shrink-0' />
                <span>{errorMessage}</span>
              </div>
            )}

            {token ? (
              /* Accept Invitation Flow */
              <button
                type='button'
                disabled={submitting}
                onClick={handleAcceptInvitation}
                className='w-full h-11 sm:h-12 bg-[#FF5612] hover:bg-[#E54D10] disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-all duration-150 flex items-center justify-center gap-2 shadow-lg cursor-pointer active:scale-98'
              >
                {submitting ? (
                  <>
                    <LoadingOutlined className='text-sm' />
                    <span>Joining Workspace...</span>
                  </>
                ) : (
                  <>
                    <span>Accept Invitation</span>
                    <ArrowRightOutlined className='text-xs' />
                  </>
                )}
              </button>
            ) : (
              /* Code Entry Flow if no token */
              <form onSubmit={handleJoinByCode} className='w-full space-y-3'>
                <input
                  type='text'
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                  placeholder='Enter Org Code (e.g. ACME-2026)'
                  className='w-full bg-[#141414] border border-[#262626] rounded-xl px-4 h-11 text-xs font-mono font-bold text-white placeholder-grayZl-600 focus:outline-none focus:border-[#FF5612] transition-colors uppercase text-center'
                />
                <button
                  type='submit'
                  disabled={submitting || !manualCode.trim()}
                  className='w-full h-11 sm:h-12 bg-[#FF5612] hover:bg-[#E54D10] disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-all duration-150 flex items-center justify-center gap-2 shadow-lg cursor-pointer active:scale-98'
                >
                  {submitting ? (
                    <>
                      <LoadingOutlined className='text-sm' />
                      <span>Validating Code...</span>
                    </>
                  ) : (
                    <>
                      <span>Accept Invitation</span>
                      <ArrowRightOutlined className='text-xs' />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default OrganizationJoinPage;
